import { useState } from "react";
import { supabase } from "./supabase.js";

const STYLE = `
  .rm-overlay {
    position: fixed; inset: 0; background: rgba(7,7,9,0.96);
    z-index: 10000; display: flex; align-items: center; justify-content: center;
    padding: 24px;
  }
  .rm-card {
    background: #0e0e12; border: 1px solid rgba(255,255,255,0.1);
    border-top: 4px solid #d42200; max-width: 480px; width: 100%;
    padding: 36px; position: relative;
  }
  .rm-eyebrow {
    font-family: 'Space Mono', monospace; font-size: 9px; letter-spacing: 0.4em;
    text-transform: uppercase; color: #d42200; margin-bottom: 8px;
  }
  .rm-heading {
    font-family: 'Bebas Neue', sans-serif; font-size: 36px; letter-spacing: 0.03em;
    color: #eeeae0; line-height: 1; margin-bottom: 10px;
  }
  .rm-sub {
    font-size: 13px; color: rgba(238,234,224,0.55); line-height: 1.65;
    margin-bottom: 28px;
  }
  .rm-field { margin-bottom: 16px; position: relative; }
  .rm-label {
    font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.15em;
    text-transform: uppercase; color: #00e67a; margin-bottom: 6px; display: block;
  }
  .rm-input {
    width: 100%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12);
    color: #eeeae0; font-family: 'Libre Baskerville', Georgia, serif; font-size: 14px;
    padding: 10px 14px; outline: none; transition: border-color 0.2s;
  }
  .rm-input:focus { border-color: rgba(255,255,255,0.3); }
  .rm-input::placeholder { color: rgba(255,255,255,0.25); }
  .rm-input:disabled { opacity: 0.35; cursor: not-allowed; }
  .rm-select { appearance: none; cursor: pointer; }
  .rm-select option { background: #13131a; color: #eeeae0; }

  .rm-dropdown {
    position: absolute; top: 100%; left: 0; right: 0; z-index: 100;
    background: #13131a; border: 1px solid rgba(255,255,255,0.15);
    border-top: none; max-height: 220px; overflow-y: auto;
    box-shadow: 0 8px 24px rgba(0,0,0,0.6);
  }
  .rm-dropdown::-webkit-scrollbar { width: 4px; }
  .rm-dropdown::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); }
  .rm-dropdown-item {
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 14px; cursor: pointer; background: none; border: none;
    width: 100%; text-align: left; color: #eeeae0; font-family: 'Libre Baskerville', Georgia, serif;
    font-size: 13px; border-bottom: 1px solid rgba(255,255,255,0.05);
    transition: background 0.1s;
  }
  .rm-dropdown-item:last-child { border-bottom: none; }
  .rm-dropdown-item:hover { background: rgba(255,255,255,0.06); }
  .rm-dropdown-state {
    font-family: 'Space Mono', monospace; font-size: 10px;
    color: rgba(255,255,255,0.35); flex-shrink: 0;
  }
  .rm-dropdown-empty {
    padding: 12px 14px; font-size: 12px; color: rgba(255,255,255,0.35);
    font-style: italic;
  }

  .rm-checkbox-row {
    display: flex; align-items: flex-start; gap: 10px; cursor: pointer;
    margin-bottom: 24px; margin-top: 4px;
  }
  .rm-checkbox-row input[type="checkbox"] {
    width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px;
    accent-color: #d42200; cursor: pointer;
  }
  .rm-checkbox-row span {
    font-size: 13px; color: rgba(238,234,224,0.75); line-height: 1.5;
  }

  .rm-save-btn {
    width: 100%; background: #d42200; color: #eeeae0;
    font-family: 'Bebas Neue', sans-serif; font-size: 22px; letter-spacing: 0.08em;
    border: none; padding: 14px; cursor: pointer; transition: background 0.15s;
    margin-bottom: 14px;
  }
  .rm-save-btn:hover:not(:disabled) { background: #e52600; }
  .rm-save-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .rm-skip {
    display: block; width: 100%; background: none; border: none;
    color: rgba(255,255,255,0.3); font-family: 'Space Mono', monospace;
    font-size: 10px; letter-spacing: 0.1em; text-align: center;
    cursor: pointer; padding: 4px 0; transition: color 0.15s;
  }
  .rm-skip:hover { color: rgba(255,255,255,0.55); }

  .rm-done {
    text-align: center; padding: 32px 0;
  }
  .rm-done-icon {
    font-size: 48px; color: #00e67a; margin-bottom: 12px;
  }
  .rm-done-text {
    font-family: 'Bebas Neue', sans-serif; font-size: 28px;
    letter-spacing: 0.06em; color: #eeeae0;
  }
`;

const METRO_REGIONS = [
  // Northeast
  { metro: "New York Metro",         state: "New York" },
  { metro: "Boston Metro",           state: "Massachusetts" },
  { metro: "Philadelphia Metro",     state: "Pennsylvania" },
  { metro: "Providence Metro",       state: "Rhode Island" },
  { metro: "Hartford Metro",         state: "Connecticut" },
  { metro: "Buffalo Metro",          state: "New York" },
  { metro: "Albany Metro",           state: "New York" },
  { metro: "Pittsburgh Metro",       state: "Pennsylvania" },
  // Mid-Atlantic
  { metro: "Washington DC Metro",    state: "District of Columbia" },
  { metro: "Baltimore Metro",        state: "Maryland" },
  { metro: "Richmond Metro",         state: "Virginia" },
  // Southeast
  { metro: "Miami Metro",            state: "Florida" },
  { metro: "Orlando Metro",          state: "Florida" },
  { metro: "Tampa Metro",            state: "Florida" },
  { metro: "Jacksonville Metro",     state: "Florida" },
  { metro: "Atlanta Metro",          state: "Georgia" },
  { metro: "Charlotte Metro",        state: "North Carolina" },
  { metro: "Raleigh-Durham Metro",   state: "North Carolina" },
  { metro: "Nashville Metro",        state: "Tennessee" },
  { metro: "Memphis Metro",          state: "Tennessee" },
  { metro: "New Orleans Metro",      state: "Louisiana" },
  { metro: "Birmingham Metro",       state: "Alabama" },
  { metro: "Louisville Metro",       state: "Kentucky" },
  // Midwest
  { metro: "Chicago Metro",          state: "Illinois" },
  { metro: "Detroit Metro",          state: "Michigan" },
  { metro: "Minneapolis Metro",      state: "Minnesota" },
  { metro: "Cleveland Metro",        state: "Ohio" },
  { metro: "Columbus Metro",         state: "Ohio" },
  { metro: "Cincinnati Metro",       state: "Ohio" },
  { metro: "Indianapolis Metro",     state: "Indiana" },
  { metro: "Milwaukee Metro",        state: "Wisconsin" },
  { metro: "Kansas City Metro",      state: "Missouri" },
  { metro: "St. Louis Metro",        state: "Missouri" },
  { metro: "Omaha Metro",            state: "Nebraska" },
  // Texas
  { metro: "Dallas Metro",           state: "Texas" },
  { metro: "Houston Metro",          state: "Texas" },
  { metro: "Austin Metro",           state: "Texas" },
  { metro: "San Antonio Metro",      state: "Texas" },
  { metro: "El Paso Metro",          state: "Texas" },
  // Mountain / Southwest
  { metro: "Denver Metro",           state: "Colorado" },
  { metro: "Phoenix Metro",          state: "Arizona" },
  { metro: "Las Vegas Metro",        state: "Nevada" },
  { metro: "Salt Lake City Metro",   state: "Utah" },
  { metro: "Albuquerque Metro",      state: "New Mexico" },
  { metro: "Tucson Metro",           state: "Arizona" },
  // California
  { metro: "Los Angeles Metro",      state: "California" },
  { metro: "San Francisco Metro",    state: "California" },
  { metro: "San Diego Metro",        state: "California" },
  { metro: "Sacramento Metro",       state: "California" },
  { metro: "San Jose Metro",         state: "California" },
  { metro: "Fresno Metro",           state: "California" },
  { metro: "Riverside Metro",        state: "California" },
  { metro: "Bakersfield Metro",      state: "California" },
  // Pacific Northwest
  { metro: "Seattle Metro",          state: "Washington" },
  { metro: "Portland Metro",         state: "Oregon" },
  // Other US
  { metro: "Honolulu Metro",         state: "Hawaii" },
  { metro: "Anchorage Metro",        state: "Alaska" },
  { metro: "Boise Metro",            state: "Idaho" },
  // Canada
  { metro: "Toronto Metro",          state: "Ontario" },
  { metro: "Vancouver Metro",        state: "British Columbia" },
  { metro: "Montreal Metro",         state: "Quebec" },
  { metro: "Calgary Metro",          state: "Alberta" },
  { metro: "Ottawa Metro",           state: "Ontario" },
  { metro: "Edmonton Metro",         state: "Alberta" },
  // UK
  { metro: "London Metro",           state: "England" },
  { metro: "Manchester Metro",       state: "England" },
  { metro: "Birmingham Metro (UK)",  state: "England" },
  { metro: "Leeds Metro",            state: "England" },
  { metro: "Glasgow Metro",          state: "Scotland" },
];

const COUNTRIES = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "New Zealand",
  "Ireland",
  "South Africa",
  "Other",
];

export default function RegionModal({ userId, onClose }) {
  const [metroSearch, setMetroSearch] = useState("");
  const [metro, setMetro] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("United States");
  const [openRegion, setOpenRegion] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const filtered = METRO_REGIONS.filter(r =>
    r.metro.toLowerCase().includes(metroSearch.toLowerCase())
  );

  function handleMetroInput(val) {
    setMetroSearch(val);
    setMetro("");
    setState("");
    setShowDropdown(true);
  }

  function handleMetroSelect(r) {
    setMetro(r.metro);
    setState(r.state);
    setMetroSearch(r.metro);
    setShowDropdown(false);
  }

  async function handleSave() {
    setSaving(true);
    await supabase.from("profiles").update({
      job_market_region:  metro || null,
      job_market_state:   state || null,
      job_market_country: country,
      job_market_open:    openRegion,
      region_set:         true,
    }).eq("id", userId);
    setSaving(false);
    setDone(true);
    setTimeout(onClose, 1400);
  }

  function handleSkip() {
    try { sessionStorage.setItem("gb_region_skipped", "1"); } catch(e) {}
    onClose();
  }

  const canSave = openRegion || metro || state;

  return (
    <>
      <style>{STYLE}</style>
      <div className="rm-overlay" onMouseDown={e => e.stopPropagation()}>
        <div className="rm-card">
          {done ? (
            <div className="rm-done">
              <div className="rm-done-icon">✓</div>
              <div className="rm-done-text">Region Saved</div>
            </div>
          ) : (
            <>
              <div className="rm-eyebrow">Job Market Setup</div>
              <h2 className="rm-heading">Where are you job hunting?</h2>
              <p className="rm-sub">We use this to show you the most relevant ghost job data for your market</p>

              <div className="rm-field">
                <label className="rm-label">Metro Area</label>
                <div style={{position:"relative"}}>
                  <input
                    className="rm-input"
                    placeholder="Search metro areas…"
                    value={metroSearch}
                    onChange={e => handleMetroInput(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    autoComplete="off"
                  />
                  {showDropdown && metroSearch && (
                    <div className="rm-dropdown">
                      {filtered.length > 0 ? filtered.map(r => (
                        <button key={r.metro} className="rm-dropdown-item" onMouseDown={() => handleMetroSelect(r)}>
                          <span>{r.metro}</span>
                          <span className="rm-dropdown-state">{r.state}</span>
                        </button>
                      )) : (
                        <div className="rm-dropdown-empty">No matches — type your city or enter manually below</div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="rm-field">
                <label className="rm-label">State / Province</label>
                <input
                  className="rm-input"
                  placeholder="Auto-filled from metro, or type manually"
                  value={state}
                  onChange={e => setState(e.target.value)}
                />
              </div>

              <div className="rm-field">
                <label className="rm-label">Country</label>
                <select className="rm-input rm-select" value={country} onChange={e => setCountry(e.target.value)}>
                  {COUNTRIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>

              <label className="rm-checkbox-row">
                <input
                  type="checkbox"
                  checked={openRegion}
                  onChange={e => setOpenRegion(e.target.checked)}
                />
                <span>I'm open to multiple regions / remote — don't lock me to one metro</span>
              </label>

              <div style={{
                background:"rgba(196,152,0,0.07)", border:"1px solid rgba(196,152,0,0.25)",
                padding:"16px 18px", marginBottom:20, marginTop:8,
              }}>
                <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:8}}>
                  <span style={{fontSize:16}}>👻</span>
                  <span style={{fontFamily:"'Space Mono',monospace", fontSize:9, letterSpacing:"0.2em", textTransform:"uppercase", color:"#c49800"}}>Founding Member</span>
                </div>
                <p style={{fontSize:12, color:"rgba(238,234,224,0.8)", lineHeight:1.75}}>
                  You're joining GhostBust at the ground floor. Every scan you run, every application outcome you log, and every ghost job you flag trains the GhostBust AI — making it smarter for every job seeker who comes after you. Early contributors like you are the reason this works. As a Founding Member, you'll have Pro access free for life when we launch paid tiers.
                </p>
              </div>

              <button className="rm-save-btn" onClick={handleSave} disabled={saving || !canSave}>
                {saving ? "Saving…" : "Save My Region →"}
              </button>
              <button className="rm-skip" onClick={handleSkip}>Skip for now</button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
